import axios, { AxiosError, AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse } from 'axios';
import { includes, pick } from 'lodash-es';
import { getRequestKey, NetworkResponse, parseResponse, transformRequest } from './helper';
import { FREEZE_CMDID, NetworkTypeEnum, NO_CANCEL_CMDID, WITHDRAWAL_CMDID } from '@star/constants';
import { useTenantStore } from '../../index';
import { showFailToast } from 'vant';

declare module 'axios' {
  interface AxiosRequestConfig {
    // isNotify?: boolean;
    // notifyType?: 'currency' | 'success' | 'error' | 'info' | 'warning' | undefined;
    // retry?: number;
    cmdId?: number;
    // headerType?: 1 | 2 | 3;
  }
}

const baseURL = '/api';
const requestCacheMap = new Map();
const CancelToken = axios.CancelToken;
axios.defaults.timeout = 30000;
axios.defaults.headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8';
axios.defaults.headers['Accept'] = 'application/json, application/xml, text/play, text/html, *.*';

const request = axios.create({
  baseURL,
});

const clearRequestCache = (response?: AxiosResponse | AxiosError) => {
  console.log();
  if (!response) {
    for (const key in requestCacheMap) {
      requestCacheMap[key]();
      requestCacheMap.delete(key);
    }
  } else {
    if (response?.config) {
      requestCacheMap.delete(response.config.headers.reqKey);
    }
  }
};

request.interceptors.request.use(config => {
  const requestKey = getRequestKey(
    config.headers.cmdId || config.url,
    config.method === 'post' ? config.data : config.params
  );

  if (!NO_CANCEL_CMDID.includes(config.headers.cmdId)) {
    config.cancelToken = new CancelToken(cancel => {
      if (requestCacheMap.has(requestKey)) {
        requestCacheMap.get(requestKey)();
      } else {
        requestCacheMap.set(requestKey, cancel);
      }
    });
  }

  const { isApiV2 = false, apiName } = config.headers;

  const { headers, data } = transformRequest(pick(config, ['headers', 'data']));

  config.headers = {
    ...headers,
    ...(import.meta.env.DEV ? { apiName } : {}),
  } as AxiosRequestHeaders;

  config.data = data;
  if (Number(config.headers.cmdId) === 371138) {
    config.url = '/v3';
  } else if (isApiV2) {
    config.url = '/v2';
  } else {
    config.url = config.url || '/v1';
  }

  if (includes(['/v1', '/v2', '/v3'], config.url)) {
    config.url = `${config.url}/`;
  }

  if (import.meta.env.DEV && apiName) {
    config.url = `${config.url}?${apiName}`;
  }

  // if (!import.meta.env.DEV && config.url.includes('/video/')) {
  //   config.url = `https://www8002-dev.wcmb86-cdn.com/api${config.url}`;
  // }

  return config;
});

request.interceptors.response.use(
  async response => {
    try {
      clearRequestCache(response);

      if (response?.config?.url?.indexOf('video') != -1) {
        return Promise.resolve(response?.data?.data || []);
      }

      const resData = await parseResponse(response);

      if (resData?.type === NetworkTypeEnum.ERROR && resData?.data?.cmdId === '371130') {
        clearRequestCache();
      }

      return Promise.resolve(resData);
    } catch (error: any) {
      // 统一处理错误
      if (
        [...FREEZE_CMDID, ...WITHDRAWAL_CMDID].includes(error.data?.cmdId || error.data?.cmd) &&
        !['数据为空'].includes(error.data?.info)
      ) {
        showFailToast(error.data?.info);
      }

      throw error;
    }
  },
  async error => {
    const config = error?.config;
    const tenantStore = useTenantStore();
    console.log(error, 'error');
    if ([500, 502].includes(error?.status) && ['371130'].includes(`${config.headers?.cmdId}`)) {
      tenantStore.setRepairStatus(true);
    }
    // const status = get(error, 'response.status', 0);

    if (config?.retry) {
      // 如果配置不存在或者重试的次数已经超过了最大限制，直接拒绝
      if (!config || config.__retryCount >= config.retry) {
        return Promise.reject(error);
      }

      // 设置重试次数
      config.__retryCount = config.__retryCount || 0;
      config.__retryCount += 1;

      // 使用增加指数回退的方法延时重试请求
      const delay = Math.pow(2, config.__retryCount) * 1000;

      await new Promise(resolve => setTimeout(resolve, delay));

      // 重新发起请求
      return request(config);
    }

    return Promise.reject(error);
  }
);

const jsonpAdapter = (config: Record<string, any>) => {
  return new Promise(function (resolve, reject) {
    const buildParams = (params: Record<string, any>) => {
      const result: any[] = [];
      for (const key in params) {
        result.push(encodeURIComponent(key) + '=' + encodeURIComponent(params[key]));
      }
      return result.join('&');
    };

    let script: any = document.createElement('script');
    let src = config.url;

    if (config.params) {
      const params = buildParams(config.params);

      if (params) {
        src += (src.indexOf('?') >= 0 ? '&' : '?') + params;
      }
    }

    script.async = true;

    function remove() {
      if (script) {
        script.onload = script.onreadystatechange = script.onerror = null;

        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }

        script = null;
      }
    }

    const jsonp = 'axiosJsonpCallback' + Date.now();
    const old = window[jsonp];
    let isAbort = false;

    window[jsonp] = function (responseData: any) {
      window[jsonp] = old;

      if (isAbort) {
        return;
      }

      const response = {
        data: responseData,
        status: 200,
      };

      resolve(response);
    };

    const additionalParams = {
      _: new Date().getTime(),
    };

    additionalParams[config.callbackParamName || 'callback'] = jsonp;

    src += (src.indexOf('?') >= 0 ? '&' : '?') + buildParams(additionalParams);

    script.onload = script.onreadystatechange = function () {
      if (!script.readyState || /loaded|complete/.test(script.readyState)) {
        remove();
      }
    };

    script.onerror = function () {
      remove();

      reject(new Error('Network Error'));
    };

    if (config.cancelToken) {
      config.cancelToken.promise.then(function (cancel: any) {
        if (!script) {
          return;
        }

        isAbort = true;

        reject(cancel);
      });
    }

    script.src = src;

    document.head.appendChild(script);
  });
};

const axiosJsonp = (url: string, params?: any) => {
  return axios({
    url,
    params,
    adapter: jsonpAdapter,
    callbackParamName: 'callback', // optional, 'callback' by default
  } as any);
};

const http = {
  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<NetworkResponse<T>> {
    return request.get(url, config);
  },

  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<NetworkResponse<T>> {
    return request.post(url, data, config);
  },
};

export { axios, axiosJsonp, request, http };
